import React, { useEffect, useMemo, useRef, useState } from 'react';
import StockDispatchHistory from '../components/StockDispatchHistory';
import WarehouseStockValidation from '../components/WarehouseStockValidation';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowDownUp,
  Boxes,
  CircleDollarSign,
  PackageCheck,
  Store,
  Truck,
  Warehouse,
} from 'lucide-react';

import StockManagerLayout from '../components/StockManagerLayout';
import InventoryProductsTable from '../components/InventoryProductsTable';
import { API_BASE_URL } from '../utils/apiConfig';
import StockExportControls from '../components/StockExportControls';
import { createCatalogTeluguResolver, getProductTeluguName } from '../utils/catalogTeluguName';
import StockTransactionsTable from '../components/StockTransactionsTable';
import TransitProductsTable from '../components/TransitProductsTable';
import { loadOutletStockCatalog } from '../utils/loadOutletStockCatalog';
import {
  clearStockManagerMessage,
  deleteInventoryProduct,
  fetchInventoryDispatchOrders,
  fetchInventoryProducts,
  fetchCatalogBarcodes,
  fetchOutlets,
  fetchStockTransactions,
  fetchWarehouses,
  rollbackInventoryProduct,
  publishInventoryProducts,
} from '../features/inventory/stockManagerInventorySlice';
import { formatStockQuantity } from '../utils/stockDisplay';
import { getNextSortConfig, sortRows } from '../utils/tableSort';
import { calculatePackPricingFromRow } from '../utils/packPricing';
import useStockDrafts from '../hooks/useStockDrafts';
import OutletStockEditor from '../components/OutletStockEditor';

const number = (value) => Number(value || 0);

const currencyFormat = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatNumber = (value) => formatStockQuantity(value);

const formatMoney = (value) => currencyFormat.format(number(value));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-IN');
};

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const locationNamesMatch = (left, right) => {
  const a = normalizeText(left);
  const b = normalizeText(right);

  if (!a || !b) return false;
  // Exact match only: a partial match made GOLLAVELLI also pick up GOLLAVELLI_OLD stock.
  return a === b;
};

const getOutletLabel = (outlet) =>
  firstValue(
    outlet?.outlet_name,
    outlet?.outletName,
    outlet?.outlet_code,
    outlet?.unit_code,
    outlet?.name,
    outlet?.location,
    outlet?.locationName,
    outlet?.id
  );

const getUserOutletName = (userInfo) =>
  firstValue(
    userInfo?.outlet?.outlet_name,
    userInfo?.outlet?.name,
    userInfo?.outlet_name,
    userInfo?.outletName,
    userInfo?.outlet,
    userInfo?.location,
    userInfo?.branch,
    userInfo?.store,
    userInfo?.storeName
  );

const getUserOutletId = (userInfo) =>
  firstValue(
    userInfo?.outlet?.id,
    userInfo?.outlet_id,
    userInfo?.outletId,
    userInfo?.location_id,
    userInfo?.locationId,
    userInfo?.store_id,
    userInfo?.storeId
  );

const getLocationId = (item, type) => {
  if (type === 'outlet') {
    return firstValue(
      item?.outlet_id,
      item?.outletId,
      item?.outletID,
      item?.business_entity_type === 'OUTLET' ? item?.business_entity_id : undefined,
      item?.store_id,
      item?.storeId
    );
  }

  return firstValue(
    item?.warehouse_id,
    item?.warehouseId,
    item?.warehouseID,
    item?.business_entity_type === 'WAREHOUSE' ? item?.business_entity_id : undefined,
    item?.inventory_id,
    item?.inventoryId
  );
};

const getLocationName = (item, type) => {
  if (type === 'outlet') {
    return firstValue(
      item?.outlet_name,
      item?.outletName,
      item?.outlet,
      item?.store_name,
      item?.storeName,
      item?.branch_name,
      item?.branchName,
      item?.location_name,
      item?.locationName,
      item?.location
    );
  }

  return firstValue(
    item?.warehouse_name,
    item?.warehouseName,
    item?.inventory_name,
    item?.inventoryName,
    item?.location_name,
    item?.locationName
  );
};

const filterByLocation = (
  items = [],
  selectedId = '',
  type = 'warehouse',
  selectedLocation = null,
  fallbackLocation = {}
) => {
  if (!selectedId) return items;

  const fallbackId = fallbackLocation?.id;
  const fallbackName = normalizeText(fallbackLocation?.name);
  const selectedName = normalizeText(
    type === 'outlet'
      ? getOutletLabel(selectedLocation)
      : firstValue(
          selectedLocation?.warehouse_name,
          selectedLocation?.warehouseName,
          selectedLocation?.warehouse_code,
          selectedLocation?.name,
          selectedLocation?.id
        )
  );

  return items.filter((item) => {
    const itemId = getLocationId(item, type);
    const itemName = normalizeText(getLocationName(item, type));
    const hasLocation = itemId !== undefined || Boolean(itemName);

    return (
      String(itemId || '') === String(selectedId) ||
      locationNamesMatch(itemName, selectedName) ||
      (!hasLocation &&
        fallbackId !== undefined &&
        String(fallbackId) === String(selectedId)) ||
      (!hasLocation && locationNamesMatch(fallbackName, selectedName))
    );
  });
};

const sumQty = (items = [], keys = []) =>
  items.reduce((sum, item) => {
    const value = keys.find((key) => item?.[key] !== undefined);
    return sum + number(value ? item[value] : 0);
  }, 0);

const getStockQty = (item) =>
  number(
    firstValue(
      item?.count_in_stock,
      item?.countInStock,
      item?.stock,
      item?.qty,
      item?.quantityInStock,
      item?.no_of_units,
      item?.purchase_qty
    )
  );

const getPurchaseUnitPrice = (item) => {
  const calculated = calculatePackPricingFromRow(item);
  if (calculated.costPerPiece > 0) return calculated.costPerPiece;

  return Math.ceil(
    number(
      firstValue(
        item?.landed_purchase_unit_price,
        item?.landedPurchaseUnitPrice,
        item?.purchase_price_with_costs,
        item?.purchasePriceWithCosts,
        item?.purchase_price,
        item?.purchasePrice,
        item?.cost_price,
        item?.costPrice,
        item?.buying_price,
        item?.buyingPrice,
        item?.unit_cost,
        item?.unitCost,
        item?.unit_price,
        item?.unitPrice,
        item?.landing_cost,
        item?.landingCost
      )
    )
  );
};

const getPurchaseAmount = (item) => {
  const directAmount = firstValue(
    item?.current_landed_purchase_amount,
    item?.currentLandedPurchaseAmount,
    item?.current_stock_landed_cost,
    item?.currentStockLandedCost,
    item?.landed_purchase_amount,
    item?.landedPurchaseAmount,
    item?.total_purchase_amount,
    item?.totalPurchaseAmount,
    item?.purchase_amount,
    item?.purchaseAmount,
    item?.total_cost,
    item?.totalCost
  );

  if (directAmount !== undefined) return number(directAmount);

  return getStockQty(item) * getPurchaseUnitPrice(item);
};

const getRawSellingUnitPrice = (item) =>
  number(firstValue(item?.dprice, item?.discounted_price, item?.discountedPrice, item?.selling_price, item?.sellingPrice, item?.salePrice));

const getSellingUnitPrice = (item) => {
  const calculated = calculatePackPricingFromRow(item);
  return calculated.dprice || getRawSellingUnitPrice(item);
};

const getMrpUnitPrice = (item) => {
  const calculated = calculatePackPricingFromRow(item);
  return calculated.mrp || number(firstValue(item?.mrp_amount, item?.mrpAmount, item?.mrp, item?.MRP, item?.unit_mrp, item?.unitMrp, item?.price));
};

const getSellingAmount = (item) => getStockQty(item) * getSellingUnitPrice(item);

const getStockBrandName = (item) =>
  firstValue(
    item?.brand,
    item?.brand_name,
    item?.brand_name_english,
    item?.brandName,
    item?.brandNameEnglish
  );

const getStockProductName = (item) =>
  firstValue(
    item?.product_name,
    item?.product_name_eng,
    item?.product_name_tel,
    item?.productName,
    item?.name,
    item?.product_id
  );

const getStockProductDisplayName = (item) => {
  const brand = String(getStockBrandName(item) || '').trim();
  const product = String(getStockProductName(item) || '').trim();

  if (!brand) return product || '-';
  if (!product) return brand;
  if (product.toLowerCase().startsWith(brand.toLowerCase())) return product;

  return `${brand} ${product}`;
};

const flattenOutletProducts = (
  products = [],
  fallbackOutletName = '',
  fallbackOutletId = ''
) =>
  asArray(products).flatMap((product) =>
    asArray(product?.details).flatMap((detail) =>
      asArray(detail?.financials).map((financial) => ({
        id: [product?._id, detail?._id, financial?._id].filter(Boolean).join(':'),
        productID: product?._id,
        brandID: detail?._id,
        financialID: financial?._id,
        catalogSource: product?.catalogSource,
        product_name: product?.name || product?.product_name || '-',
        product_name_tel: getProductTeluguName(product),
        product_barcode_id: financial?.product_barcode_id || financial?.catalogProductBarcodeId || financial?.mkid,
        mk_barcode: financial?.mk_barcode,
        product_code: product?.product_code || product?.productCode || '-',
        brand: detail?.brand || '-',
        sku_id: financial?.mkid || financial?.sku_id || '-',
        bar_code: asArray(financial?.barcode)[0] || '-',
        countInStock: financial?.countInStock,
        outlet_id: firstValue(
          financial?.outlet_id,
          financial?.outletId,
          detail?.outlet_id,
          detail?.outletId,
          product?.outlet_id,
          product?.outletId,
          product?.business_entity_type === 'OUTLET'
            ? product?.business_entity_id
            : undefined,
          fallbackOutletId
        ),
        outlet_name: firstValue(
          financial?.outlet_name,
          financial?.outletName,
          financial?.outlet,
          financial?.location,
          financial?.locationName,
          detail?.outlet_name,
          detail?.outletName,
          detail?.outlet,
          detail?.location,
          detail?.locationName,
          product?.outlet_name,
          product?.outletName,
          product?.outlet,
          product?.store_name,
          product?.storeName,
          product?.branch_name,
          product?.branchName,
          product?.location_name,
          product?.locationName,
          product?.location,
          fallbackOutletName
        ),
        quantity: financial?.quantity,
        units: financial?.units,
        price: financial?.price,
        dprice: financial?.dprice,
        costPrice: financial?.costPrice,
        cost_price: financial?.cost_price,
        mrp: financial?.price,
        unit_mrp: financial?.unit_mrp,
        mfg_date: financial?.mfg_date,
        exp_date: financial?.exp_date,
      }))
    )
  );

const statusIsTransit = (status) =>
  ['sent', 'packed', 'label_printed', 'dispatched', 'intransit'].includes(
    String(status || '').toLowerCase()
  );

const SummaryCard = ({ icon: Icon, label, value, detail, tone }) => {
  const tones = {
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
    green: 'border-green-200 bg-green-50 text-green-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-900',
    slate: 'border-slate-200 bg-white text-slate-900',
  };

  return (
    <div className={`rounded-lg border p-4 shadow-sm ${tones[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold opacity-75">{label}</p>
          <p className="mt-2 text-2xl font-bold">{value}</p>
          <p className="mt-1 text-xs opacity-70">{detail}</p>
        </div>
        <Icon size={24} />
      </div>
    </div>
  );
};

const tabs = [
  { id: 'outlets', label: 'Outlet Stock' },
  { id: 'inventory', label: 'Inventory Stock' },
  { id: 'transactions', label: 'Stock Transactions' },
  { id: 'transit', label: 'Products In Transit' },
];

const outletColumns = [
  {
    key: 'product',
    label: 'Product',
    align: 'left',
    getValue: getStockProductDisplayName,
  },
  { key: 'product_telugu', label: 'Telugu name', align: 'left', getValue: getProductTeluguName },
  { key: 'brand', label: 'Brand', align: 'left', getValue: (item) => item.brand },
  {
    key: 'outlet',
    label: 'Outlet',
    align: 'left',
    getValue: (item) => getLocationName(item, 'outlet') || getLocationId(item, 'outlet'),
  },
  { key: 'sku', label: 'SKU', align: 'left', getValue: (item) => item.sku_id },
  { key: 'barcode', label: 'Barcode', align: 'left', getValue: (item) => item.bar_code },
  { key: 'quantity', label: 'Quantity', align: 'right', type: 'number', getValue: (item) => item.quantity },
  { key: 'units', label: 'Units', align: 'left', getValue: (item) => item.units },
  {
    key: 'stock',
    label: 'Stock',
    align: 'right',
    type: 'number',
    getValue: getStockQty,
  },
  {
    key: 'purchaseUnit',
    label: 'Selling / Unit',
    align: 'right',
    type: 'number',
    getValue: getSellingUnitPrice,
  },
  {
    key: 'purchaseStock',
    label: 'Selling / Stock',
    align: 'right',
    type: 'number',
    getValue: getSellingAmount,
  },
  {
    key: 'mrpUnit',
    label: 'MRP / Unit',
    align: 'right',
    type: 'number',
    getValue: getMrpUnitPrice,
  },
  {
    key: 'mfgDate',
    label: 'MFG Date',
    align: 'left',
    getValue: (item) => item.mfg_date,
  },
  {
    key: 'expDate',
    label: 'Expiry Date',
    align: 'left',
    getValue: (item) => item.exp_date,
  },
];

const SortHeader = ({ column, sortConfig, onSort }) => (
  <th className={`p-3 ${column.align === 'right' ? 'text-right' : 'text-left'}`}>
    <button
      type="button"
      onClick={() => onSort(column.key)}
      className={`inline-flex w-full items-center gap-1 font-semibold hover:text-blue-700 ${
        column.align === 'right' ? 'justify-end' : 'justify-start'
      }`}
    >
      <span>{column.label}</span>
      <span className="text-xs">
        {sortConfig.key === column.key
          ? sortConfig.direction === 'asc'
            ? '^'
            : 'v'
          : '-'}
      </span>
    </button>
  </th>
);

const OutletStockTable = ({
  products = [],
  catalogBarcodes = [],
  loading,
  outlets = [],
  selectedOutletId = '',
  onOutletChange,
  onRefresh,
  token,
  onInventoryEdit,
}) => {
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'product',
    direction: 'asc',
  });

  const filteredProducts = useMemo(() => {
    const value = search.toLowerCase();
    const resolveTelugu = createCatalogTeluguResolver(catalogBarcodes);

    return products.map(item => ({ ...item, product_name_tel: resolveTelugu(item) })).filter((item) =>
      [
        item.product_name,
        getProductTeluguName(item),
        getStockProductDisplayName(item),
        item.product_code,
        item.sku_id,
        item.bar_code,
        item.brand,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(value))
    );
  }, [products, catalogBarcodes, search]);

  const sortedProducts = useMemo(
    () => sortRows(filteredProducts, sortConfig, outletColumns),
    [filteredProducts, sortConfig]
  );

  const handleSort = (key) => {
    setSortConfig((current) => getNextSortConfig(current, key));
  };

  const exportColumns = outletColumns.map(column => ({
    ...column,
    financial: ['purchaseUnit', 'purchaseStock', 'mrpUnit'].includes(column.key),
    width: { product: 48, product_telugu: 48, brand: 28, outlet: 30, sku: 65, barcode: 30, quantity: 18, units: 16, stock: 18,
      purchaseUnit: 26, purchaseStock: 28, mrpUnit: 22, mfgDate: 24, expDate: 24 }[column.key],
    getValue: item => {
      const value = column.getValue(item);
      if (['purchaseUnit', 'purchaseStock', 'mrpUnit'].includes(column.key)) return formatMoney(value);
      if (['mfgDate', 'expDate'].includes(column.key)) return formatDate(value);
      if (column.key === 'stock') return formatNumber(value);
      if (column.key === 'quantity') return value ?? '-';
      return value || '-';
    },
  }));
  const selectedOutlet = outlets.find(outlet => String(outlet.id) === String(selectedOutletId));

  return (
    <section className="rounded-xl border bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold">Outlet Stock</h2>
          <button type="button" onClick={onRefresh} disabled={loading}
            className="rounded border px-3 py-2 text-sm disabled:opacity-50">
            Refresh
          </button>
        </div>

        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
          <select
            value={selectedOutletId}
            onChange={(event) => onOutletChange(event.target.value)}
            className="w-full rounded-lg border px-3 py-2 md:w-56"
          >
            <option value="">All outlets</option>
            {outlets.map((outlet) => (
              <option key={outlet.id} value={outlet.id}>
                {getOutletLabel(outlet)}
              </option>
            ))}
          </select>

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search outlet product / SKU / barcode"
            className="w-full rounded-lg border px-3 py-2 md:w-80"
          />
        </div>
      </div>

      <StockExportControls columns={exportColumns} rows={sortedProducts} loading={loading}
        location={selectedOutletId ? (selectedOutlet ? getOutletLabel(selectedOutlet) : selectedOutletId) : 'All outlets'} search={search} />

      {loading ? (
        <p className="text-gray-500">Loading outlet stock...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                {outletColumns.map((column) => (
                  <SortHeader
                    key={column.key}
                    column={column}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                ))}
                <th className="p-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {sortedProducts.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{getStockProductDisplayName(item)}</td>
                  <td className="p-3" lang="te">{getProductTeluguName(item) || '-'}</td>
                  <td className="p-3">{item.brand}</td>
                  <td className="p-3">
                    {getLocationName(item, 'outlet') || getLocationId(item, 'outlet') || '-'}
                  </td>
                  <td className="p-3">{item.sku_id}</td>
                  <td className="p-3">{item.bar_code}</td>
                  <td className="p-3 text-right">{item.quantity ?? '-'}</td>
                  <td className="p-3">{item.units || '-'}</td>
                  <td className="p-3 text-right font-semibold">
                    {formatNumber(getStockQty(item))}
                  </td>
                  <td className="p-3 text-right font-semibold">
                    {formatMoney(getSellingUnitPrice(item))}
                  </td>
                  <td className="p-3 text-right font-semibold">
                    {formatMoney(getSellingAmount(item))}
                  </td>
                  <td className="p-3 text-right font-semibold">
                    {formatMoney(getMrpUnitPrice(item))}
                  </td>
                  <td className="p-3">{formatDate(item.mfg_date)}</td>
                  <td className="p-3">{formatDate(item.exp_date)}</td>
                  <td className="p-3"><OutletStockEditor item={item} token={token}
                    onSaved={onRefresh} onInventoryEdit={onInventoryEdit} /></td>
                </tr>
              ))}

              {sortedProducts.length === 0 && (
                <tr>
                  <td colSpan={outletColumns.length + 1} className="p-4 text-center text-gray-500">
                    No outlet stock products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const StockPage = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('outlets');
  const [historyItem, setHistoryItem] = useState(null);
  const [selectedOutletId, setSelectedOutletId] = useState('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  const { userInfo } = useSelector((state) => state.posUser || {});
  const [outletProductsRaw, setOutletProductsRaw] = useState([]);
  const [outletProductsLoading, setOutletProductsLoading] = useState(false);
  const [outletStockError, setOutletStockError] = useState('');
  const [outletRefresh, setOutletRefresh] = useState(0);
  // Background refresh: never interrupt a load that is still running, and wait at least
  // 2 minutes between automatic reloads so a slow backend can finish.
  const outletLoadInFlight = useRef(false);
  const outletLastLoadedAt = useRef(0);
  const outletHasData = useRef(false);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'hidden' && navigator.onLine !== false
        && !outletLoadInFlight.current && Date.now() - outletLastLoadedAt.current >= 120000) {
        setOutletRefresh(value => value + 1);
      }
    };
    const timer = window.setInterval(refresh, 120000);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);
  useEffect(() => {
    if (!userInfo?.token) return;
    const controller = new AbortController();
    outletLoadInFlight.current = true;
    // Keep showing the current table during background refreshes; only show
    // "Loading outlet stock..." when there is nothing to show yet.
    if (!outletHasData.current) setOutletProductsLoading(true);
    setOutletStockError('');
    loadOutletStockCatalog(userInfo, controller.signal)
      .then(products => {
        if (controller.signal.aborted) return;
        setOutletProductsRaw(products);
        outletHasData.current = true;
      })
      .catch(error => { if (!controller.signal.aborted) setOutletStockError(error.message); })
      .finally(() => {
        outletLoadInFlight.current = false;
        outletLastLoadedAt.current = Date.now();
        if (!controller.signal.aborted) setOutletProductsLoading(false);
      });
    return () => controller.abort();
  }, [userInfo, outletRefresh]);
  const {
    inventoryProducts = [],
    catalogBarcodes = [],
    transactions = [],
    inventoryDispatchOrders = [],
    transitProducts = [],
    warehouses = [],
    outlets = [],
    loading = false,
    error,
    successMessage,
  } = useSelector((state) => state.stockManagerInventory || {});
  const stockDrafts = useStockDrafts(userInfo?._id || userInfo?.id || userInfo?.email || userInfo?.username, inventoryProducts);
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');
  const draftCount = Object.keys(stockDrafts.drafts).length;

  useEffect(() => {
    if (!userInfo?.token) return;

    dispatch(fetchInventoryProducts());
    dispatch(fetchCatalogBarcodes());
    dispatch(fetchStockTransactions());
    dispatch(fetchInventoryDispatchOrders());
    dispatch(fetchOutlets());
    dispatch(fetchWarehouses());
  }, [dispatch, userInfo]);

  useEffect(() => {
    if (!error && !successMessage) return undefined;

    const timer = setTimeout(() => {
      dispatch(clearStockManagerMessage());
    }, 3000);

    return () => clearTimeout(timer);
  }, [dispatch, error, successMessage]);

  const refreshInventory = () => {
    dispatch(fetchInventoryProducts());
    dispatch(fetchCatalogBarcodes());
    dispatch(fetchStockTransactions());
  };

  const handleInventoryEdit = async (inventoryProductId, changes) => {
    if (publishing) throw new Error('Wait for publishing to finish.');
    setPublishMessage('');
    return stockDrafts.stage(inventoryProductId, changes);
  };

  const publishDrafts = async () => {
    if (publishing || !draftCount) return;
    setPublishing(true);
    setPublishMessage('');
    try {
      const result = await dispatch(publishInventoryProducts(Object.values(stockDrafts.drafts))).unwrap();
      stockDrafts.remove((result.products || []).map(row => row.id));
      const failures = result.failures || [];
      setPublishMessage(`${result.products?.length || 0} stock changes published.` +
        (failures.length ? ` ${failures.length} drafts retained: ${failures.map(row => `${row.inventoryProductId}: ${row.message}`).join('; ')}` : ''));
      if (result.products?.length) refreshInventory();
    } catch (error) {
      setPublishMessage(`Publish failed. Drafts are retained. ${error?.message || error}`);
    } finally { setPublishing(false); }
  };

  const handleInventoryDelete = async (inventoryProductId, reason) => {
    await dispatch(deleteInventoryProduct({ inventoryProductId, reason })).unwrap();
    refreshInventory();
  };

  const handleInventoryRollback = async (inventoryProductId, reason) => {
    await dispatch(rollbackInventoryProduct({ inventoryProductId, reason })).unwrap();
    refreshInventory();
  };

  const summary = useMemo(() => {
    const selectedOutlet = outlets.find(
      (outlet) => String(outlet.id) === String(selectedOutletId)
    );
    const selectedWarehouse = warehouses.find(
      (warehouse) => String(warehouse.id) === String(selectedWarehouseId)
    );
    const userOutletName = getUserOutletName(userInfo);
    const userOutletId = getUserOutletId(userInfo);
    const outletProducts = flattenOutletProducts(
      outletProductsRaw,
      userOutletName,
      userOutletId
    );
    const selectedOutletProducts = filterByLocation(
      outletProducts,
      selectedOutletId,
      'outlet',
      selectedOutlet,
      {
        id: userOutletId,
        name: userOutletName,
      }
    );
    const selectedInventoryProducts = filterByLocation(
      stockDrafts.preview,
      selectedWarehouseId,
      'warehouse',
      selectedWarehouse
    );
    const productsInTransit = inventoryDispatchOrders
      .filter((order) => statusIsTransit(order.dispatch_status))
      .flatMap((order) => order.items || []);

    const inventoryStockQty = selectedInventoryProducts.reduce(
      (sum, item) => sum + getStockQty(item),
      0
    );
    const outletStockQty = selectedOutletProducts.reduce(
      (sum, item) => sum + getStockQty(item),
      0
    );
    const inventoryPurchaseAmount = selectedInventoryProducts.reduce(
      (sum, item) => sum + getPurchaseAmount(item),
      0
    );
    const outletPurchaseAmount = selectedOutletProducts.reduce(
      (sum, item) => sum + getPurchaseAmount(item),
      0
    );
    const transitQty =
      transitProducts.length > 0
        ? sumQty(transitProducts, ['qty', 'quantity', 'no_of_units'])
        : sumQty(productsInTransit, ['qty', 'quantity', 'no_of_units']);

    return {
      outletProducts,
      selectedOutletProducts,
      selectedInventoryProducts,
      productsCount: selectedInventoryProducts.length,
      outletProductsCount: selectedOutletProducts.length,
      stockQty: inventoryStockQty,
      outletStockQty,
      totalStockQty: inventoryStockQty + outletStockQty,
      inventoryPurchaseAmount,
      outletPurchaseAmount,
      totalPurchaseAmount: inventoryPurchaseAmount + outletPurchaseAmount,
      transactionsCount: transactions.length,
      transitCount:
        transitProducts.length > 0 ? transitProducts.length : productsInTransit.length,
      transitQty,
    };
  }, [
    outletProductsRaw,
    stockDrafts.preview,
    selectedOutletId,
    selectedWarehouseId,
    outlets,
    warehouses,
    userInfo,
    transactions,
    inventoryDispatchOrders,
    transitProducts,
  ]);

  return (
    <StockManagerLayout>
      <section className="space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stock</h1>
            <p className="text-sm text-gray-500">
              Stock across 2 outlets and inventory, with total stock and purchase value.
            </p>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg bg-red-100 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        ) : null}

        {successMessage ? (
          <div className="rounded-lg bg-green-100 px-4 py-3 text-sm font-semibold text-green-700">
            {successMessage}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            icon={Boxes}
            label="Total Stock"
            value={formatNumber(summary.totalStockQty)}
            detail={`${formatNumber(summary.outletStockQty)} outlet + ${formatNumber(
              summary.stockQty
            )} inventory`}
            tone="blue"
          />
          <SummaryCard
            icon={CircleDollarSign}
            label="Total Purchase Amount"
            value={formatMoney(summary.totalPurchaseAmount)}
            detail="Outlet and inventory purchase value"
            tone="green"
          />
          <SummaryCard
            icon={Store}
            label="Outlet Stock"
            value={formatNumber(summary.outletStockQty)}
            detail={`${formatNumber(summary.outletProductsCount)} product lines`}
            tone="amber"
          />
          <SummaryCard
            icon={Warehouse}
            label="Inventory Stock"
            value={formatNumber(summary.stockQty)}
            detail={`${formatNumber(summary.productsCount)} product lines`}
            tone="indigo"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SummaryCard
            icon={PackageCheck}
            label="Inventory Purchase"
            value={formatMoney(summary.inventoryPurchaseAmount)}
            detail="Purchase value in inventory"
            tone="slate"
          />
          <SummaryCard
            icon={ArrowDownUp}
            label="Transactions"
            value={formatNumber(summary.transactionsCount)}
            detail={`In transit: ${formatNumber(summary.transitQty)} qty`}
            tone="slate"
          />
          <SummaryCard
            icon={Truck}
            label="In Transit"
            value={formatNumber(summary.transitCount)}
            detail={`Transit qty: ${formatNumber(summary.transitQty)}`}
            tone="slate"
          />
        </div>

        <div className="flex flex-wrap gap-2 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-4 py-2 text-sm font-semibold ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'outlets' && outletStockError ? <p role="alert" className="text-red-600">{outletStockError}</p> : null}
        {activeTab === 'outlets' ? (
          <OutletStockTable
            catalogBarcodes={catalogBarcodes}
            products={summary.selectedOutletProducts}
            loading={outletProductsLoading}
            outlets={outlets}
            selectedOutletId={selectedOutletId}
            onOutletChange={setSelectedOutletId}
            onRefresh={() => setOutletRefresh(value => value + 1)}
            token={userInfo?.token}
            onInventoryEdit={() => { setActiveTab('inventory'); setSelectedWarehouseId('');
              setPublishMessage('Vadaparru stock is managed by inventory batches. Edit the matching SKU here and publish the changes.'); }}
          />
        ) : null}

        {activeTab === 'inventory' ? (
          <>
          <WarehouseStockValidation products={stockDrafts.preview} warehouses={warehouses} onSave={handleInventoryEdit} disabled={publishing || loading} />
          <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="font-semibold">{draftCount} unpublished stock changes</p>
            <p className="text-sm">Edits are saved on this browser until you publish. Publish includes all warehouses. Delete and rollback remain immediate actions.</p>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={publishDrafts} disabled={publishing || !draftCount || loading}
                className="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white disabled:opacity-50">
                {publishing ? 'Publishing...' : `Publish changes (${draftCount})`}
              </button>
              <button type="button" disabled={publishing || !draftCount} onClick={() => {
                if (window.confirm('Discard all unpublished stock changes from this browser?')) {
                  try { stockDrafts.discard(); setPublishMessage('Drafts discarded.'); }
                  catch (error) { setPublishMessage(error.message); }
                }
              }} className="rounded-lg border px-4 py-2">Discard drafts</button>
            </div>
            {publishMessage && <p role="status" className="mt-2 text-sm">{publishMessage}</p>}
            {draftCount > 0 && <details className="mt-2 text-sm">
              <summary className="cursor-pointer">Review drafts</summary>
              <ul className="mt-2 space-y-2">{Object.values(stockDrafts.drafts).map(draft => (
                <li key={draft.inventoryProductId}>
                  {draft.name} (#{draft.inventoryProductId}): Stock {draft.changes.count_in_stock}, purchase price {draft.changes.landed_purchase_unit_price}, MRP {draft.changes.unit_mrp}, expiry {draft.changes.exp_date || 'none'}
                  {draft.changes.cascade_dispatch_financials && ' • Update dispatch prices'}
                  {draft.changes.sync_pos_mongo && ' • Update POS prices'}
                  <button type="button" disabled={publishing} className="ml-3 underline" onClick={() => {
                    try { stockDrafts.remove([draft.inventoryProductId]); }
                    catch (error) { setPublishMessage(error.message); }
                  }}>Discard item draft</button>
                </li>
              ))}</ul>
            </details>}
          </div>
          <InventoryProductsTable
            onHistory={setHistoryItem}
            products={summary.selectedInventoryProducts.filter(row=>Number(row.count_in_stock ?? row.no_of_units ?? 0)>0)}
            catalogBarcodes={catalogBarcodes}
            loading={loading || publishing}
            warehouses={warehouses}
            selectedWarehouseId={selectedWarehouseId}
            onWarehouseChange={setSelectedWarehouseId}
            onEdit={handleInventoryEdit}
            onReference={async (inventoryId,enabled)=>{
              try {
                const response=await fetch(`${API_BASE_URL}/inventory-pg/pricing-workspace/reference`,{method:'POST',headers:{Authorization:`Bearer ${userInfo?.token}`,'Content-Type':'application/json'},body:JSON.stringify({inventoryId,enabled})});
                const result=await response.json();if(!response.ok)throw Error(result.message||'Unable to save reference');
                await dispatch(fetchInventoryProducts()).unwrap();
              } catch(error){window.alert(error.message);}
            }}
            onEditMany={entries => {
              if (publishing) throw new Error('Wait for publishing to finish.');
              setPublishMessage('');
              return stockDrafts.stageMany(entries);
            }}
            onDelete={draftCount ? undefined : handleInventoryDelete}
            onRollback={draftCount ? undefined : handleInventoryRollback}
            draftMode
          />
          </>
        ) : null}

        {historyItem && <StockDispatchHistory item={historyItem} token={userInfo?.token} onClose={() => setHistoryItem(null)} />}
        {activeTab === 'transactions' ? (
          <StockTransactionsTable transactions={transactions} />
        ) : null}

        {activeTab === 'transit' ? (
          <TransitProductsTable
            transitProducts={transitProducts}
            dispatchOrders={inventoryDispatchOrders}
            loading={loading}
          />
        ) : null}
      </section>
    </StockManagerLayout>
  );
};

export default StockPage;
